import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Billing result — handles ?session_id=… polling from Stripe.
 * Polls /billing/checkout/status/{sid} up to 6 times (10s).
 */
const T = {
  bg: "#FCFAF3", text: "#003049", textMuted: "rgba(0,48,73,0.65)",
  primary: "#F77F00", primaryGlow: "rgba(247,127,0,0.45)",
  cardBg: "#FFFFFF", border: "#EEE6CE",
};

export default function BillingResult({ result }) {
  const isSuccessRoute = result === "success";
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState(isSuccessRoute && sessionId ? "polling" : (isSuccessRoute ? "ok" : "cancel"));
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!isSuccessRoute || !sessionId) return;
    let stop = false;
    const tick = async () => {
      try {
        attempts.current += 1;
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        if (stop) return;
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setPhase("ok");
          return;
        }
        if (r.data.status === "expired" || r.data.status === "cancelled") {
          setPhase("expired");
          return;
        }
        if (attempts.current >= 6) {
          setPhase("timeout");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPhase("error");
      }
    };
    tick();
    return () => { stop = true; };
  }, [sessionId, isSuccessRoute]);

  const heading = {
    ok: "You're in.",
    polling: "Confirming your payment…",
    timeout: "Still processing…",
    expired: "Checkout expired",
    cancel: "Checkout cancelled",
    error: "Could not verify payment",
  }[phase];

  const sub = {
    ok: data?.plan_id
      ? `Your ${data.plan_id === "self_prep_ai" ? "Self Prep AI" : "Hybrid Live"} plan is now active. Welcome aboard.`
      : "Your plan is being activated.",
    polling: "Hold on — we're talking to Stripe. This usually takes a few seconds.",
    timeout: "Stripe is still processing. You'll see your plan activate in your dashboard shortly.",
    expired: "Your checkout session expired. You can try again anytime.",
    cancel: "No payment was taken. You can try again anytime.",
    error: "Something went wrong while checking your payment. Please contact support if you were charged.",
  }[phase];

  const showOk = phase === "ok";
  const showLoad = phase === "polling";

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-result-root">
      <div className="relative max-w-md w-full rounded-sm p-10 text-center"
        style={{ background: T.cardBg, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 60px -30px rgba(0,48,73,0.20)` }}>
        <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: T.primary }} />
        <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: T.primary }} />
        <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: T.primary }} />
        <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: T.primary }} />

        {showLoad && <Loader2 className="w-14 h-14 mx-auto mb-4 animate-spin" style={{ color: T.primary }} data-testid="billing-loading" />}
        {showOk && <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#16A34A" }} data-testid="billing-success" />}
        {!showOk && !showLoad && <XCircle className="w-14 h-14 mx-auto mb-4" style={{ color: phase === "cancel" ? T.textMuted : "#D62828" }} data-testid="billing-error" />}

        <h1 className="font-bold text-3xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>{heading}</h1>
        <p className="mt-3 text-sm" style={{ color: T.textMuted }}>{sub}</p>
        {data?.amount_total != null && (
          <div className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
            CHARGED: {(data.amount_total / 100).toFixed(2)} {(data.currency || "USD").toUpperCase()}
          </div>
        )}

        <Link to="/app" data-testid="billing-dashboard-btn"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-sm font-bold uppercase tracking-[0.2em] text-sm transition-all"
          style={{ background: T.primary, color: "#fff", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
          Go to dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
